import React from 'react'
import { Link } from 'react-router-dom'
import { Checkbox, Tooltip } from 'antd'
import take from 'lodash/take'
import { EmailInviteStatusEnum } from '@vacationtracker/shared/types/user'
import FormattedDate from '@vacationtracker/shared/components/formatted-date'

import IntlMessages from '../../../../util/IntlMessages'
import { StatusBadge } from '../../../../design-system/StatusBadge'
import { IUserFilteredInfo } from '../../../../types/users'
import { IGetLabelsShort } from '../../../../types/labels'
import { columnHelper } from './tableSetup'
import { COLUMN_IDS } from './columnIds'
import { SHARED_COLUMN_WIDTHS, actionsColumn } from './sharedColumns'
import { NameCell } from '../components/NameCell'
import { IRowActionsMenuProps } from '../components/RowActionsMenu'
import { EditableCell, DateEditor, emptyCellDisplay } from '../components/EditableCell'
import { ConfirmFilterEditor, ConfirmTextEditor } from '../components/ConfirmEditors'
import type { IMoveTargets } from './moveTargetOptions'
import { LabelsEditor } from '../components/LabelsEditor'

export interface IProfileColumnsEditingContext {
  canEditCell: (row: IUserFilteredInfo, columnId: string) => boolean
  getLockReason: (row: IUserFilteredInfo, columnId: string) => string | undefined
  teams: { id: string; name: string }[]
  locations: { id: string; name: string }[]
  allLabels: IGetLabelsShort[]
  commitTeam: (user: IUserFilteredInfo, teamId: string) => Promise<void>
  commitLocation: (user: IUserFilteredInfo, locationId: string) => Promise<void>
  commitStartDate: (user: IUserFilteredInfo, startDate: string) => Promise<void>
  commitEmployeeId: (user: IUserFilteredInfo, employeeId: string) => Promise<void>
  commitBirthday: (user: IUserFilteredInfo, birthday: string) => Promise<void>
  locationChangeNote: (firstName: string, locationName: string) => string
  toggleLabel: (user: IUserFilteredInfo, label: IGetLabelsShort, assigned: boolean) => Promise<void>
}

export const PROFILE_COLUMN_WIDTHS: Record<string, number> = {
  ...SHARED_COLUMN_WIDTHS,
  [COLUMN_IDS.select]: 40,
  [COLUMN_IDS.location]: 180,
  [COLUMN_IDS.role]: 210,
  [COLUMN_IDS.labels]: 200,
  [COLUMN_IDS.status]: 110,
  [COLUMN_IDS.startDate]: 140,
  [COLUMN_IDS.email]: 220,
  [COLUMN_IDS.employeeId]: 180,
  [COLUMN_IDS.birthday]: 140,
}

export interface IProfileColumnsContext {
  dateFormat: string
  companyPlatform?: string
  showLabels: boolean
  showSelection: boolean
  getInviteStatus: (user: IUserFilteredInfo) => EmailInviteStatusEnum
  approverUserIds: Set<string>
  rowMenu: Omit<IRowActionsMenuProps, 'user'>
  editing: IProfileColumnsEditingContext
  /** The department and location pickers, built like the filter bar's. */
  moveTargets: IMoveTargets
  showEnergy?: boolean
  onSelectAll?: () => void
}

export function buildProfileColumns(context: IProfileColumnsContext) {
  const { dateFormat, companyPlatform, showLabels, showSelection, getInviteStatus, approverUserIds, rowMenu, editing, moveTargets, showEnergy, onSelectAll } = context

  return columnHelper.columns([
    ...(showSelection
      ? [
          columnHelper.display({
            id: COLUMN_IDS.select,
            enableHiding: false,
            header: ({ table }) => (
              <Checkbox
                checked={table.getIsAllRowsSelected()}
                indeterminate={table.getIsSomeRowsSelected()}
                onChange={() => {
                  if (!table.getIsAllRowsSelected()) {
                    onSelectAll?.()
                  }
                  table.toggleAllRowsSelected()
                }}
              />
            ),
            cell: ({ row }) => <Checkbox checked={row.getIsSelected()} disabled={!row.getCanSelect()} onClick={row.getToggleSelectedHandler()} />,
          }),
        ]
      : []),
    columnHelper.accessor('name', {
      id: COLUMN_IDS.name,
      enableHiding: false,
      header: () => <IntlMessages id="app.name" />,
      cell: ({ row }) => <NameCell user={row.original} companyPlatform={companyPlatform} getInviteStatus={getInviteStatus} showEnergy={showEnergy} />,
    }),
    columnHelper.accessor((row) => row.team?.name, {
      id: COLUMN_IDS.team,
      header: () => <IntlMessages id="app.department" />,
      cell: ({ row }) => (
        <EditableCell<string>
          cellKey={`${row.original.id}:${COLUMN_IDS.team}`}
          lockReason={editing.getLockReason(row.original, COLUMN_IDS.team)}
          editable={editing.canEditCell(row.original, COLUMN_IDS.team) && editing.teams.length > 1}
          affordance="chevron"
          display={<>{row.original.team?.name}</>}
          renderEditor={({ commit, cancel }) => (
            <ConfirmFilterEditor value={row.original.team?.id} label={row.original.team?.name ?? ''} targets={moveTargets.teams} commit={commit} cancel={cancel} />
          )}
          onCommit={(teamId) => editing.commitTeam(row.original, teamId)}
        />
      ),
    }),
    columnHelper.accessor((row) => row.location?.name, {
      id: COLUMN_IDS.location,
      header: () => <IntlMessages id="app.location" />,
      cell: ({ row }) => (
        <EditableCell<string>
          cellKey={`${row.original.id}:${COLUMN_IDS.location}`}
          lockReason={editing.getLockReason(row.original, COLUMN_IDS.location)}
          editable={editing.canEditCell(row.original, COLUMN_IDS.location) && editing.locations.length > 1}
          affordance="chevron"
          display={<>{row.original.location?.name}</>}
          renderEditor={({ commit, cancel }) => (
            <ConfirmFilterEditor
              value={row.original.location?.id}
              label={row.original.location?.name ?? ''}
              targets={moveTargets.locations}
              commit={commit}
              cancel={cancel}
              // Brought-forward days are derived from the location, so moving someone reworks their
              // leave. A department move does not, and says nothing.
              noteFor={(locationName) => editing.locationChangeNote(row.original.firstName || row.original.name.split(' ')[0], locationName)}
            />
          )}
          onCommit={(locationId) => editing.commitLocation(row.original, locationId)}
        />
      ),
    }),
    columnHelper.accessor('role', {
      id: COLUMN_IDS.role,
      header: () => <IntlMessages id="users.table.roles" />,
      cell: ({ row }) => renderRoles(row.original, approverUserIds),
    }),
    ...(showLabels
      ? [
          columnHelper.accessor((row) => row.labels, {
            id: COLUMN_IDS.labels,
            header: () => <IntlMessages id="app.labels" />,
            cell: ({ row }) => (
              <LabelsEditor
                cellKey={`${row.original.id}:${COLUMN_IDS.labels}`}
                allLabels={editing.allLabels}
                userLabels={row.original.labels || []}
                display={renderLabels(row.original.labels || [], row.original.id)}
                editable={editing.canEditCell(row.original, COLUMN_IDS.labels)}
                lockReason={editing.getLockReason(row.original, COLUMN_IDS.labels)}
                onToggle={(label, assigned) => editing.toggleLabel(row.original, label, assigned)}
              />
            ),
          }),
        ]
      : []),
    columnHelper.accessor('status', {
      id: COLUMN_IDS.status,
      header: () => <IntlMessages id="app.status" />,
      cell: ({ row }) => renderStatus(row.original.status),
    }),
    columnHelper.accessor('startDate', {
      id: COLUMN_IDS.startDate,
      header: () => <IntlMessages id="users.startDate" />,
      cell: ({ row }) => (
        <EditableCell<string>
          cellKey={`${row.original.id}:${COLUMN_IDS.startDate}`}
          lockReason={editing.getLockReason(row.original, COLUMN_IDS.startDate)}
          editable={editing.canEditCell(row.original, COLUMN_IDS.startDate)}
          affordance="calendar"
          display={row.original.startDate ? <FormattedDate value={row.original.startDate} format={dateFormat} /> : <IntlMessages id="app.notSet" />}
          renderEditor={({ commit, cancel }) => (
            <DateEditor value={row.original.startDate} format={dateFormat} commit={commit} cancel={cancel} requiredMessage={<IntlMessages id="users.table.startDateRequired" />} />
          )}
          onCommit={(startDate) => editing.commitStartDate(row.original, startDate)}
        />
      ),
    }),
    columnHelper.accessor('email', {
      id: COLUMN_IDS.email,
      header: () => <IntlMessages id="app.email" />,
      cell: ({ row }) => (
        <div className="vt-users-cell">
          <span className="vt-users-cell-value">{row.original.email}</span>
        </div>
      ),
    }),
    columnHelper.accessor((row) => row.employeeId, {
      id: COLUMN_IDS.employeeId,
      header: () => <IntlMessages id="user.employeeId" />,
      cell: ({ row }) => (
        <EditableCell<string>
          cellKey={`${row.original.id}:${COLUMN_IDS.employeeId}`}
          lockReason={editing.getLockReason(row.original, COLUMN_IDS.employeeId)}
          editable={editing.canEditCell(row.original, COLUMN_IDS.employeeId)}
          affordance="pencil"
          display={row.original.employeeId ? <>{row.original.employeeId}</> : emptyCellDisplay(editing.canEditCell(row.original, COLUMN_IDS.employeeId), 'user.employeeId')}
          renderEditor={({ commit, cancel }) => <ConfirmTextEditor value={row.original.employeeId} commit={commit} cancel={cancel} ariaLabel="Edit employee ID" />}
          onCommit={(employeeId) => editing.commitEmployeeId(row.original, employeeId)}
        />
      ),
    }),
    columnHelper.accessor((row) => row.birthday, {
      id: COLUMN_IDS.birthday,
      header: () => <IntlMessages id="user.birthday" />,
      cell: ({ row }) => (
        <EditableCell<string>
          cellKey={`${row.original.id}:${COLUMN_IDS.birthday}`}
          lockReason={editing.getLockReason(row.original, COLUMN_IDS.birthday)}
          editable={editing.canEditCell(row.original, COLUMN_IDS.birthday)}
          affordance="calendar"
          display={
            row.original.birthday ? (
              <FormattedDate value={row.original.birthday} format={dateFormat} />
            ) : (
              emptyCellDisplay(editing.canEditCell(row.original, COLUMN_IDS.birthday), 'user.birthday')
            )
          }
          renderEditor={({ commit, cancel }) => <DateEditor value={row.original.birthday} format={dateFormat} commit={commit} cancel={cancel} clearable />}
          onCommit={(birthday) => editing.commitBirthday(row.original, birthday)}
        />
      ),
    }),
    actionsColumn(rowMenu),
  ])
}

function renderRoles(user: IUserFilteredInfo, approverUserIds: Set<string>): React.ReactElement {
  const parts: React.ReactNode[] = []
  if (user.role === 'Admin') {
    parts.push(<IntlMessages key="admin" id="app.role.administrator" />)
  }
  if (user.role === 'Approver' || approverUserIds.has(user.id)) {
    parts.push(<IntlMessages key="approver" id="app.role.approver" />)
  }
  if (user.roles?.includes('LOCATION_ADMIN')) {
    parts.push(<IntlMessages key="locAdmin" id="app.role.locationAdmin" />)
  }
  if (parts.length === 0) {
    return <IntlMessages id="app.user" />
  }
  const joined = parts.map((part, index) => (
    <React.Fragment key={index}>
      {index > 0 && <span>, </span>}
      {part}
    </React.Fragment>
  ))
  if (parts.length === 1) {
    return <>{joined}</>
  }
  return (
    <Tooltip title={joined}>
      <span>{joined}</span>
    </Tooltip>
  )
}

function renderLabels(labels: IGetLabelsShort[], userId: string): React.ReactElement {
  const visibleBadges = take(labels, 3).map((label) => (
    <StatusBadge key={label.id} color={label.color} shape="pill">
      {label.name}
    </StatusBadge>
  ))
  if (labels.length <= 3) {
    return <>{visibleBadges}</>
  }
  return (
    <Tooltip placement="topLeft" title={labels.map((label) => label.name).join(', ')}>
      <span>
        {visibleBadges}
        <Link to={`/app/users/${userId}`}>{`+${labels.length - 3}`}</Link>
      </span>
    </Tooltip>
  )
}

function renderStatus(status: string): React.ReactElement {
  if (status === 'ACTIVE') {
    return (
      <StatusBadge tone="success">
        <IntlMessages id="app.active" />
      </StatusBadge>
    )
  }
  if (status === 'INACTIVE') {
    return (
      <StatusBadge tone="warning">
        <IntlMessages id="app.inactive" />
      </StatusBadge>
    )
  }
  return (
    <StatusBadge tone="danger">
      <IntlMessages id="app.deleted" />
    </StatusBadge>
  )
}
